/**
 * French social security number (NIR) helpers: input formatting, normalization
 * and structural + checksum validation.
 *
 * Pure, UI-agnostic functions — `validateSsn` returns a stable error code and
 * the caller maps it to a localized message (i18n stays in the UI layer).
 */

/** Character groups of a French NIR (sex / year / month / department / commune
 *  / order / key), used to space the number for readability. */
const SSN_GROUPS = [1, 2, 2, 2, 3, 3, 2]

/** Validation outcome: a stable error code, or `null` when the NIR is valid. */
export type SsnError = 'required' | 'format' | 'sex' | 'month' | 'department' | 'key'

/**
 * i18n key per validation code. It lives here, beside the codes it maps, rather than in the
 * component that happens to need it: the connected space now asks for a NIR too, and a second
 * copy of this table would drift from the first the day a rule is added to `validateSsn`.
 *
 * The keys stay under `subscription.info.errors` because that is where the messages already
 * are, and they say the same thing wherever the number is typed. Moving them would rename
 * strings for no gain.
 */
export const SSN_ERROR_KEYS: Record<SsnError, string> = {
  required: 'subscription.info.errors.ssn',
  format: 'subscription.info.errors.ssnInvalid',
  sex: 'subscription.info.errors.ssnSex',
  month: 'subscription.info.errors.ssnMonth',
  department: 'subscription.info.errors.ssnDepartment',
  key: 'subscription.info.errors.ssnKey',
}

/** Normalize a NIR: strip spaces/hyphens and uppercase (for the Corsican
 *  departments 2A/2B). Canonical form used to validate and persist. */
export function normalizeSsn(raw: string): string {
  return raw.replace(/[\s-]+/g, '').toUpperCase()
}

/** Format a NIR for display while typing: keep only significant characters
 *  (digits + the A/B of the Corsican departments, max 15) and group them per
 *  `SSN_GROUPS`. The grouping is character-based, so a "2A"/"2B" department
 *  occupies its slot like any two-character group. */
export function formatSsn(raw: string): string {
  const chars = raw.toUpperCase().replace(/[^0-9AB]/g, '').slice(0, 15)
  const parts: string[] = []
  let i = 0
  for (const len of SSN_GROUPS) {
    if (i >= chars.length) break
    parts.push(chars.slice(i, i + len))
    i += len
  }
  return parts.join(' ')
}

/**
 * Validate a French NIR according to the business rules, from the most
 * structural check to the finest. Returns the first failing rule's code, or
 * `null` when the number is valid.
 */
export function validateSsn(raw: string): SsnError | null {
  const value = normalizeSsn(raw)
  // Provided?
  if (!value) return 'required'
  // 15 characters: 13 digits + a department that may be 2A/2B (Corsica).
  if (!/^\d{5}(?:\d{2}|2[AB])\d{8}$/.test(value)) return 'format'
  // Sex: 1 (male) or 2 (female).
  if (value[0] !== '1' && value[0] !== '2') return 'sex'
  // Birth month: 01 to 12.
  const month = Number(value.slice(3, 5))
  if (month < 1 || month > 12) return 'month'
  // Department: 01-95, 2A/2B (Corsica) or 97-99 (96 is explicitly rejected).
  const dept = value.slice(5, 7)
  const deptNumber = Number(dept)
  const deptValid =
    dept === '2A' || dept === '2B' || (deptNumber >= 1 && deptNumber <= 95) || (deptNumber >= 97 && deptNumber <= 99)
  if (!deptValid) return 'department'
  // Check digits = 97 - (first 13 digits mod 97). For Corsica, 2A becomes 19
  // and 2B becomes 18 before the computation.
  const base = value.slice(0, 13).replace('2A', '19').replace('2B', '18')
  const key = 97 - (Number(base) % 97)
  if (key !== Number(value.slice(13, 15))) return 'key'
  return null
}
